import { toast } from 'react-toastify';
import { IErrorModel, IPaginatedResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import returnsService from 'services/api/returns.service';
import { Shipment, UpdateShipmentAmountBody } from '@core/models/shipment.model';
import shipmentsService from 'services/api/shipments.service';

// -------------------------------------------------------------------------


interface IShipmentState {
    loading: boolean;
    updateShipmentAmountLoading: boolean;
    error: IErrorModel | undefined;
    shipments: Shipment
}

const initialState: IShipmentState = {
    loading: false,
    updateShipmentAmountLoading: false,
    error: {} as IErrorModel,
    shipments: {} as Shipment,
};

// -------------------------------------------------------------------------

export const getShipmentsThunk = createAsyncThunk<Shipment, void, { rejectValue: IErrorModel }>(
    `/shipment/getShipments`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await shipmentsService.getShipments();
            // Handle response structure: { "Shipment": { id, balance, lastCost } }
            return response.data.Shipment || response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch shipments', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);


// -------------------------------------------------------------------------

export const updateShipmentAmountThunk = createAsyncThunk<Shipment, UpdateShipmentAmountBody, { rejectValue: IErrorModel }>(
    `/shipment/updateShipmentAmount`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await shipmentsService.updateShipment(body);
            toast.success(response.data.message || 'Shipment updated successfully', { autoClose: 5000 });
            return response.data;
        }
        catch (err: any) {
            toast.error(err.errorMessage || 'Failed to update shipment amount', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);


// -------------------------------------------------------------------------

const shipmentSlice = createSlice({
    name: `shipment`,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Get Shipments
        builder.addCase(getShipmentsThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getShipmentsThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.shipments = action.payload;
        });
        builder.addCase(getShipmentsThunk.rejected, (state: any, action: any) => {
            state.loading = false;
            state.shipments = {} as Shipment;
            state.error = action.payload;
        });
        // Update Shipment Amount
        builder.addCase(updateShipmentAmountThunk.pending, (state) => {
            state.updateShipmentAmountLoading = true;
        });
        builder.addCase(updateShipmentAmountThunk.fulfilled, (state: any, action: any) => {
            state.updateShipmentAmountLoading = false;
            // Refresh shipments after update
            state.shipments = action.payload.Shipment || action.payload;
        });
        builder.addCase(updateShipmentAmountThunk.rejected, (state: any, action: any) => {
            state.updateShipmentAmountLoading = false;
            state.error = action.payload;
        });

    }
});

// -------------------------------------------------------------------------

export default shipmentSlice;
export const shipmentReducer = shipmentSlice.reducer;

